/**
 * Chain context — the URL ⇄ state primitive (Sequential Gate G2, pure layer).
 *
 * The single resolver for "what chain am I in?", derived from the ROUTE, not React
 * state — so it is correct on the server, on deep-link, and after back/forward
 * navigation. The chain is a path segment (`/<hubSlug>`), so the URL IS the state.
 *
 * These functions are pure and server-safe (no React, no `'use client'`). The thin
 * client hook + provider live in `src/components/hub/ChainContextProvider.tsx`.
 *
 * See docs/superpowers/specs/hub-build-scope/04-sequential-gate-and-contracts.md §2/§3.
 */

import { getChain, listChains, type ChainId, type ChainMeta } from '@/lib/chains';
import type { ChainSelectorValue } from '@/types/hub';

/** Top-level hub sub-views (allowlisted segments under `/<hubSlug>/<view>`). */
export const HUB_VIEWS = ['tokens', 'nfts', 'people', 'intel'] as const;
export type HubView = (typeof HUB_VIEWS)[number];

/** Is `view` a known hub sub-view segment? */
export function isHubView(view: string | undefined | null): view is HubView {
  return typeof view === 'string' && (HUB_VIEWS as readonly string[]).includes(view);
}

/**
 * Map a route hubSlug segment → a chain id. The registry keys by `id` (e.g.
 * `avalanche`) but the hub route uses `hubSlug` (e.g. `avax`), so a plain
 * `getChain(segment)` would miss `avax`. This resolves either form.
 *
 * Returns the canonical chain id, or undefined for an unknown segment.
 */
export function hubSlugToChainId(segment: string | undefined | null): ChainId | undefined {
  if (!segment || typeof segment !== 'string') return undefined;
  const lower = segment.toLowerCase();
  // Fast path: the segment is itself a chain id (covers most chains where id === hubSlug).
  const direct = getChain(lower);
  if (direct) return direct.id;
  // Fall back to a hubSlug lookup (e.g. 'avax' → 'avalanche').
  const byHubSlug = listChains().find((c) => c.hubSlug.toLowerCase() === lower);
  return byHubSlug?.id;
}

/**
 * Resolve the active chain from a pathname.
 *
 * - `/tags`, `/`, `/market`, … (no chain hub segment) → `'all'` (neutral aggregate).
 * - `/<hubSlug>` or `/<hubSlug>/<view>` where hubSlug resolves to a chain → that ChainId.
 * - An unknown first segment → `'all'` (the route guard, not this resolver, 404s it).
 *
 * Pure + server-safe. The first path segment is the only thing inspected.
 */
export function resolveChainFromPath(pathname: string | undefined | null): ChainSelectorValue {
  if (!pathname || typeof pathname !== 'string') return 'all';
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'all';
  const chainId = hubSlugToChainId(segments[0]);
  return chainId ?? 'all';
}

/**
 * Extract the active hub sub-view from a pathname (`/<hubSlug>/<view>` → view),
 * or undefined for the hub overview / a non-hub route. Used to preserve the active
 * tab when switching chains via the selector.
 */
export function resolveViewFromPath(pathname: string | undefined | null): HubView | undefined {
  if (!pathname || typeof pathname !== 'string') return undefined;
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2) return undefined;
  // Only a real chain hub has a sub-view.
  if (!hubSlugToChainId(segments[0])) return undefined;
  return isHubView(segments[1]) ? segments[1] : undefined;
}

/**
 * Build the nav target for the selector.
 *
 * - `'all'` → strips the chain segment, returning the neutral aggregate `/tags`.
 * - a specific chain → `/<hubSlug>`, PRESERVING the active hub sub-view when switching
 *   between hubs (`/base/tokens` switch→Abstract ⇒ `/abstract/tokens`).
 *
 * `currentPathname` is read only to preserve the active tab; pass `''`/undefined for a
 * bare hub link. Unknown target → `/tags` (safe neutral fallback).
 *
 * Pure + server-safe.
 */
export function chainHref(
  target: ChainSelectorValue,
  currentPathname?: string | null,
): string {
  if (target === 'all') return '/tags';
  const chain = getChain(target);
  if (!chain) return '/tags';
  const view = resolveViewFromPath(currentPathname);
  return view ? `/${chain.hubSlug}/${view}` : `/${chain.hubSlug}`;
}

/** The resolved chain context value (registry meta when a specific chain is active). */
export interface ChainContextValue {
  /** The active chain id, or `'all'` on the neutral aggregate. */
  chain: ChainSelectorValue;
  /** Registry meta for the active chain (undefined when `chain === 'all'`). */
  meta?: ChainMeta;
}

/** Resolve the full chain context (value + registry meta) from a pathname. Pure. */
export function resolveChainContext(pathname: string | undefined | null): ChainContextValue {
  const chain = resolveChainFromPath(pathname);
  return { chain, meta: chain === 'all' ? undefined : getChain(chain) };
}
